import { commonValues } from '@/config/common';

export default defineNuxtPlugin((nuxtApp) => {
  const { locales } = nuxtApp.$i18n;

  // Use existing language mapping from common config
  const LANGUAGE_SUBDOMAIN_MAP = commonValues.LANGUAGEMAPPING;

  // Default hostname for processed URLs
  const DEFAULT_HOSTNAME = 'www.hostelworld.com';

  // Cache for language path map to avoid repeated calls
  let cachedLanguagePathMap = null;

  /**
   * Get language path mapping from i18n configuration
   * @returns {object} Language code to path mapping
   */
  const getLanguagePathMap = () => {
    // Return cached value if available
    if (cachedLanguagePathMap) {
      return cachedLanguagePathMap;
    }

    const fallbackLocalesValues = {
      pt: 'pt',
      es: 'es',
      fr: 'fr',
      de: 'de',
      it: 'it',
      sv: 'sv',
      pl: 'pl',
      fi: 'fi',
      da: 'da',
      nl: 'nl',
      no: 'no',
      cs: 'cs',
      ru: 'ru',
      tr: 'tr',
      zh: 'zh',
      ja: 'ja',
      ko: 'ko',
      br: 'br',
      // English and main domain don't get a language prefix
      en: '',
      www: '',
    };

    try {
      // Check if we're in a Nuxt environment
      if (typeof window !== 'undefined' || process.env.NODE_ENV === 'test') {
        // In test environment or client-side, use fallback
        cachedLanguagePathMap = fallbackLocalesValues;
        return fallbackLocalesValues;
      }

      // Get the locales value once and validate it
      const localesValue = locales?.value;

      // Check if locales is available and valid
      if (!localesValue || !Array.isArray(localesValue) || localesValue.length === 0) {
        // Fallback to static mapping if i18n is not available or locales is empty
        cachedLanguagePathMap = fallbackLocalesValues;
        return fallbackLocalesValues;
      }

      // At this point, we know localesValue is a valid non-empty array
      // Build path mapping from i18n locales configuration
      const pathMap = {};

      localesValue.forEach((locale) => {
        const {
          code,
          url,
        } = locale;

        if (code === 'en') {
          // English doesn't get a language prefix
          pathMap[code] = '';
          pathMap.www = '';
        } else {
          // Use the url field from i18n config
          pathMap[code] = url;
        }
      });

      // Cache the result
      cachedLanguagePathMap = pathMap;
      return pathMap;
    } catch (error) {
      console.error('### Error getting language path map from i18n:', error);

      // Fallback to static mapping
      cachedLanguagePathMap = fallbackLocalesValues;
      return fallbackLocalesValues;
    }
  };

  const replaceUrlCharacters = (url) => {
    return url ? url?.replace(/[\W_]+/g, '-')?.toLowerCase() : '';
  };

  const addSlashOnEnd = (value) => {
    return !value || value.endsWith('/') ? value : `${value}/`;
  };

  const buildUrlQuery = (url, queryParams) => {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return url;
    }
    const params = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    const separator = url && url.includes('?') ? '&' : '?';
    return `${url}${separator}${params}`;
  };

  const setHref = (str, target = false) => {
    if (target)
      return window.open(str, '_blank');
    window.location.href = str;
  };

  /**
   * Extracts URLs from HTML content that contain /st/ paths
   * @param {string} htmlContent - HTML content to search for URLs
   * @returns {Array} Array of found URLs
   */
  const extractStUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return [];
    }

    const urlRegex = /https?:\/\/[^\s"']*\/st\/[^\s"']*/gi;
    const matches = htmlContent.match(urlRegex) || [];

    return [...new Set(matches)]; // Remove duplicates
  };

  /**
   * Detects if a URL contains a language subdomain
   * @param {string} url - URL to check
   * @returns {object} Object with isLanguageSubdomain boolean and detected language
   */
  const detectLanguageSubdomain = (url) => {
    if (!url || typeof url !== 'string') {
      return {
        isLanguageSubdomain: false,
        language: null,
      };
    }

    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      // Check if it's a language subdomain
      for (const [subdomain, languageCode] of Object.entries(LANGUAGE_SUBDOMAIN_MAP)) {
        if (hostname.startsWith(`${subdomain}.`)) {
          return {
            isLanguageSubdomain: true,
            language: languageCode,
            subdomain,
          };
        }
      }

      // Check for www subdomain (main domain)
      if (hostname.startsWith('www.')) {
        return {
          isLanguageSubdomain: false,
          language: 'en',
          subdomain: 'www',
        };
      }

      return {
        isLanguageSubdomain: false,
        language: null,
      };
    } catch (error) {
      console.error('### Error parsing URL:', error);
      return {
        isLanguageSubdomain: false,
        language: null,
      };
    }
  };

  /**
   * Processes a single URL according to the specified rules
   * @param {string} url - URL to process
   * @returns {object} Object with original URL, processed URL, and processing details
   */
  const processUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return {
        originalUrl: url,
        processedUrl: url,
        hasStPath: false,
        isLanguageSubdomain: false,
        detectedLanguage: null,
        processingDetails: 'Invalid URL provided',
      };
    }

    try {
      const urlObj = new URL(url);
      const originalUrl = url;
      const originalPathname = urlObj.pathname; // Store original pathname for transformation display

      // Check if URL contains /st/ path
      const hasStPath = urlObj.pathname.includes('/st/');

      if (!hasStPath) {
        return {
          originalUrl,
          processedUrl: url,
          hasStPath: false,
          isLanguageSubdomain: false,
          detectedLanguage: null,
          processingDetails: 'No /st/ path found',
        };
      }

      // Detect language subdomain
      const {
        isLanguageSubdomain,
        language,
        subdomain,
      } = detectLanguageSubdomain(url);

      // Replace subdomain with default hostname
      if (isLanguageSubdomain) {
        urlObj.hostname = DEFAULT_HOSTNAME;
      }

      // Process the /st/ path
      let newPathname = urlObj.pathname;
      if (hasStPath) {
        const LANGUAGE_PATH_MAP = getLanguagePathMap();
        const languagePath = LANGUAGE_PATH_MAP[language] || '';

        if (languagePath) {
          // Replace /st/ with /{language}/
          newPathname = urlObj.pathname.replace('/st/', `/${languagePath}/`);
        } else {
          // For English/main domain, remove /st/ completely
          newPathname = urlObj.pathname.replace('/st/', '/');
        }
      }

      urlObj.pathname = newPathname;
      const processedUrl = urlObj.toString();

      return {
        originalUrl,
        processedUrl,
        hasStPath: true,
        isLanguageSubdomain,
        detectedLanguage: language,
        processingDetails: {
          subdomainReplaced: isLanguageSubdomain ? `${subdomain}.hostelworld.com` : null,
          newHostname: DEFAULT_HOSTNAME,
          pathTransformation: `${originalPathname} → ${newPathname}`,
          languageCode: language,
        },
      };
    } catch (error) {
      console.error('### Error processing URL:', error);
      return {
        originalUrl: url,
        processedUrl: url,
        hasStPath: false,
        isLanguageSubdomain: false,
        detectedLanguage: null,
        processingDetails: `Error processing URL: ${error.message}`,
      };
    }
  };

  /**
   * Processes all URLs in HTML content
   * @param {string} htmlContent - HTML content containing URLs
   * @returns {object} Object with original content, processed content, and processing results
   */
  const processHtmlContent = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return {
        originalContent: htmlContent,
        processedContent: htmlContent,
        processedUrls: [],
        processingSummary: 'No content to process',
      };
    }

    const stUrls = extractStUrls(htmlContent);
    let processedContent = htmlContent;
    const processedUrls = [];

    // Process each URL found
    stUrls.forEach((url) => {
      const result = processUrl(url);
      processedUrls.push(result);

      // Replace the URL in the content
      if (result.processedUrl !== result.originalUrl) {
        processedContent = processedContent.replace(result.originalUrl, result.processedUrl);
      }
    });

    const processingSummary = {
      totalUrlsFound: stUrls.length,
      urlsProcessed: processedUrls.length,
      urlsModified: processedUrls.filter((r) => r.processedUrl !== r.originalUrl).length,
      languagesDetected: [...new Set(processedUrls.map((r) => r.detectedLanguage).filter(Boolean))],
    };

    return {
      originalContent: htmlContent,
      processedContent,
      processedUrls,
      processingSummary,
    };
  };

  /**
   * Batch process multiple HTML content items
   * @param {Array} contentItems - Array of objects with content and metadata
   * @returns {Array} Array of processing results
   */
  const batchProcessContent = (contentItems) => {
    if (!Array.isArray(contentItems)) {
      return [];
    }

    return contentItems.map((item, index) => {
      const result = processHtmlContent(item.content || item.description || item);

      return {
        index,
        id: item.id || index,
        ...result,
      };
    });
  };

  /**
   * Utility function to get language mapping information
   * @returns {object} Language mapping information
   */
  const getLanguageMappings = () => {
    return {
      subdomainMap: LANGUAGE_SUBDOMAIN_MAP,
      pathMap: getLanguagePathMap(),
    };
  };

  /**
   * Clear the cached language path map
   * Useful for testing or when i18n configuration changes
   */
  const clearLanguagePathMapCache = () => {
    cachedLanguagePathMap = null;
  };

  return {
    provide: {
      url: {
        replaceUrlCharacters,
        addSlashOnEnd,
        buildUrlQuery,
        setHref,
        extractStUrls,
        detectLanguageSubdomain,
        processUrl,
        processHtmlContent,
        batchProcessContent,
        getLanguageMappings,
        clearLanguagePathMapCache,
      },
    },
  };
});
